// Regulated · shield emblem flanked by compliance certificates
// (extracted from HeroArtwork.jsx; behaviour preserved)
import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { glassCard } from "./_shared";

function RegulatedArt({ accent }) {
  const { t } = useTranslation();
  return (
    <div
      className="relative w-full aspect-square max-w-[460px] mx-auto overflow-hidden"
      data-testid="hero-art-regulated"
      style={{ perspective: "1400px" }}
    >
      {/* Halo · inset to keep the blur inside the artwork stage */}
      <div
        aria-hidden
        className="absolute inset-[14%] rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle at 50% 50%, color-mix(in srgb, ${accent} 26%, transparent) 0%, transparent 70%)`,
          filter: "blur(10px)",
        }}
      />

      {/* THE LICENSE CERTIFICATE CARD · centrepiece, tilted in 3D */}
      <motion.div
        className="absolute rounded-2xl overflow-hidden"
        style={{
          left: "9%", top: "9%", width: "76%", aspectRatio: "1.32",
          background: `
            linear-gradient(160deg,
              #0b1c1b 0%,
              #0d2422 35%,
              #0a1a19 70%,
              #061211 100%)`,
          border: `1px solid color-mix(in srgb, ${accent} 50%, transparent)`,
          boxShadow: `
            0 30px 70px -22px color-mix(in srgb, ${accent} 38%, transparent),
            0 14px 36px -14px rgba(0,0,0,0.6),
            inset 0 1px 0 color-mix(in srgb, ${accent} 38%, transparent),
            inset 0 -2px 0 rgba(0,0,0,0.3)`,
          transform: "rotateY(-8deg) rotateX(5deg)",
          transformStyle: "preserve-3d",
        }}
        animate={{ y: [0, -3, 0] }}
        transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut" }}
      >
        {/* Inner certificate border */}
        <div
          aria-hidden
          className="absolute inset-3 rounded-lg pointer-events-none"
          style={{
            border: `1px solid color-mix(in srgb, ${accent} 28%, transparent)`,
            boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${accent} 8%, transparent)`,
          }}
        />
        {/* Subtle guilloché pattern · concentric circles for premium feel */}
        <svg aria-hidden viewBox="0 0 320 240" className="absolute inset-0 w-full h-full opacity-[0.12] pointer-events-none">
          {[...Array(7)].map((_, i) => (
            <circle key={i} cx="160" cy="118" r={36 + i * 14}
                    fill="none" stroke={accent} strokeWidth="0.35" />
          ))}
        </svg>

        {/* Diagonal shimmer */}
        <motion.div
          aria-hidden
          className="absolute inset-y-0 pointer-events-none"
          style={{
            width: "28%",
            background: `linear-gradient(105deg, transparent 35%, color-mix(in srgb, ${accent} 20%, rgba(255,255,255,0.2)) 50%, transparent 65%)`,
            mixBlendMode: "screen",
          }}
          animate={{ x: ["-120%", "320%"] }}
          transition={{ duration: 4.2, repeat: Infinity, ease: "easeInOut", repeatDelay: 2.4 }}
        />

        {/* Header */}
        <div className="absolute inset-x-0 top-4 text-center px-3">
          <div className="font-mono font-bold uppercase tracking-[0.32em]"
               style={{ fontSize: 7.5, color: accent }}>
            {t("heroArt.certificateOfLicensing", "Certificate of Licensing")}
          </div>
          <div className="font-display font-bold mt-1.5"
               style={{ fontSize: 14, color: "rgba(255,255,255,0.96)", letterSpacing: "-0.01em" }}>
            {t("heroArt.ofinancialGroup", "OFinancial Group")}
          </div>
          <div className="font-mono mt-0.5"
               style={{ fontSize: 7.5, color: "rgba(255,255,255,0.55)", letterSpacing: "0.22em" }}>
            {t("heroArt.authorizedAndLicensed", "AUTHORIZED & LICENSED")}
          </div>
        </div>

        {/* Centre · the seal */}
        <div className="absolute inset-0 grid place-items-center" style={{ paddingTop: 4 }}>
          <div className="relative grid place-items-center" style={{ width: 92, height: 92 }}>
            {/* Outer hairline-text ring · rotates */}
            <motion.svg
              viewBox="0 0 100 100"
              className="absolute inset-[-14px] w-[120px] h-[120px] pointer-events-none"
              animate={{ rotate: 360 }}
              transition={{ duration: 36, repeat: Infinity, ease: "linear" }}
            >
              <defs>
                <path id="reg-ring-v4"
                      d="M 50,50 m -42,0 a 42,42 0 1,1 84,0 a 42,42 0 1,1 -84,0"
                      fill="none" />
              </defs>
              <text fontFamily="ui-monospace, Menlo, Monaco, monospace"
                    fontSize="5" letterSpacing="1.4"
                    fill={`color-mix(in srgb, ${accent} 80%, transparent)`}>
                <textPath href="#reg-ring-v4" startOffset="0">
                  ★ GLOBAL COVERAGE ★ LICENSED ★ EST 2025 ★ ★ GLOBAL COVERAGE ★ LICENSED ★ EST 2025 ★
                </textPath>
              </text>
            </motion.svg>
            {/* The seal disc itself */}
            <motion.div
              className="relative rounded-full grid place-items-center"
              style={{
                width: 78, height: 78,
                background: `radial-gradient(circle at 30% 28%, color-mix(in srgb, ${accent} 42%, #0c1f1e) 0%, color-mix(in srgb, ${accent} 18%, #061212) 65%, #050d0d 100%)`,
                border: `2px solid color-mix(in srgb, ${accent} 60%, transparent)`,
                boxShadow: `0 0 30px color-mix(in srgb, ${accent} 36%, transparent),
                            inset 0 1px 0 rgba(255,255,255,0.1),
                            inset 0 -2px 0 rgba(0,0,0,0.4)`,
              }}
              animate={{ scale: [1, 1.04, 1] }}
              transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
            >
              {/* Inner ring */}
              <div
                aria-hidden
                className="absolute inset-2 rounded-full pointer-events-none"
                style={{ border: `1px solid color-mix(in srgb, ${accent} 32%, transparent)` }}
              />
              <ShieldCheck size={36} strokeWidth={1.6} style={{ color: accent }} />
            </motion.div>
          </div>
        </div>

        {/* Footer · license details + wax seal */}
        <div className="absolute inset-x-0 bottom-4 flex items-end justify-between px-5">
          <div className="font-mono leading-tight">
            <div className="uppercase" style={{ fontSize: 7, color: "rgba(255,255,255,0.5)", letterSpacing: "0.22em" }}>
              {t("heroArt.licensedJurisdictions", "Licensed Jurisdictions")}
            </div>
            <div className="font-bold mt-0.5" style={{ fontSize: 12, color: accent, letterSpacing: "0.04em" }}>
              Antigua · Mauritius
            </div>
            <div className="mt-0.5" style={{ fontSize: 7.5, color: "rgba(255,255,255,0.5)", letterSpacing: "0.06em" }}>
              {t("heroArt.regulatedGroupSub", "Authorized financial group · Global coverage")}
            </div>
          </div>
          {/* Wax seal */}
          <div
            className="rounded-full grid place-items-center shrink-0"
            style={{
              width: 38, height: 38,
              background: "radial-gradient(circle at 32% 28%, #c84242 0%, #8a2424 55%, #4f1010 100%)",
              border: "1.5px solid #6d1818",
              boxShadow: "0 4px 10px -4px rgba(160,40,40,0.45), inset 0 1px 0 rgba(255,210,210,0.25), inset 0 -1px 0 rgba(40,8,8,0.6)",
            }}
          >
            <div className="font-display font-black" style={{ fontSize: 9, color: "#fbe5e5", letterSpacing: "0.06em", textShadow: "0 1px 0 rgba(0,0,0,0.45)" }}>
              OF
            </div>
          </div>
        </div>
      </motion.div>

      {/* Floating compliance pill 1 · Segregated funds (bottom-left) */}
      <motion.div
        className="absolute rounded-xl px-3 py-2 flex items-center gap-2"
        style={{
          ...glassCard(accent, 93),
          left: "7%", bottom: "9%", width: "42%",
          transform: "rotateY(10deg) rotateX(-4deg)",
        }}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: [0, -3, 0] }}
        transition={{
          opacity: { duration: 0.5, delay: 0.4 },
          y: { duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.8 },
        }}
      >
        <div className="w-7 h-7 rounded-md grid place-items-center shrink-0"
             style={{
               background: "color-mix(in srgb, #54d97c 14%, transparent)",
               border: "1px solid color-mix(in srgb, #54d97c 45%, transparent)",
             }}>
          <Check size={13} strokeWidth={2.4} style={{ color: "#54d97c" }} />
        </div>
        <div className="min-w-0 flex-1 leading-tight">
          <div className="font-mono font-bold uppercase tracking-wide truncate" style={{ fontSize: 8.5, color: "var(--text-1)" }}>
            {t("heroArt.segregatedFunds", "Segregated Funds")}
          </div>
          <div className="font-mono mt-0.5 truncate" style={{ fontSize: 8, color: "var(--text-3)" }}>
            {t("heroArt.tier1Custody", "Tier-1 bank custody")}
          </div>
        </div>
      </motion.div>

      {/* Floating compliance pill 2 · Negative balance (top-right) */}
      <motion.div
        className="absolute rounded-xl px-3 py-2 flex items-center gap-2"
        style={{
          ...glassCard(accent, 93),
          right: "7%", top: "9%", width: "42%",
          transform: "rotateY(-12deg) rotateX(4deg)",
        }}
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: [0, -3, 0] }}
        transition={{
          opacity: { duration: 0.5, delay: 0.6 },
          y: { duration: 5.6, repeat: Infinity, ease: "easeInOut", delay: 1.4 },
        }}
      >
        <div className="w-7 h-7 rounded-md grid place-items-center shrink-0"
             style={{
               background: `color-mix(in srgb, ${accent} 14%, transparent)`,
               border: `1px solid color-mix(in srgb, ${accent} 50%, transparent)`,
             }}>
          <ShieldCheck size={13} strokeWidth={2.2} style={{ color: accent }} />
        </div>
        <div className="min-w-0 flex-1 leading-tight">
          <div className="font-mono font-bold uppercase tracking-wide truncate" style={{ fontSize: 8.5, color: "var(--text-1)" }}>
            {t("heroArt.negBalProtected", "Neg-Bal Protected")}
          </div>
          <div className="font-mono mt-0.5 truncate" style={{ fontSize: 8, color: "var(--text-3)" }}>
            {t("heroArt.allRetailAccounts", "All retail accounts")}
          </div>
        </div>
      </motion.div>
    </div>
  );
}


export default RegulatedArt;
